import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/buttons/Button/Button";
import { Modal } from "@/components/ui/overlays/Modal/Modal";
import { useToast } from "@/components/ui/status/Toast/useToast";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { getProfilesListListQueryKey } from "@/components/uploads/UploadsPage";
import type { BizProfileModels } from "@/data/bizProfile/bizProfile.models";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProfiles: BizProfileModels.BizProfileListDto[];
  pagination: {
    limit: number;
    order?: string;
    page?: number;
    cursor?: string;
    filterExperienceYearsMax?: number;
    filterExperienceYearsMin?: number;
    filterRating?: BizProfileModels.BizProfileListFilterRatingParam;
    filterMsaLocation?: string;
    filterTagName?: BizProfileModels.BizProfileListFilterTagNameParam;
    filterJobTitle?: string;
    filterSearch?: string;
  };
  selectAllResumes: boolean;
  setSelectAllResumes: (value: boolean) => void;
  totalProfiles: number;
}

const UploadsPageDeleteProfilesModal = ({
  isOpen,
  onClose,
  selectedProfiles,
  pagination,
  selectAllResumes,
  setSelectAllResumes,
  totalProfiles,
}: IProps) => {
  const queryClient = useQueryClient();
  const { successToast, errorToast } = useToast();
  const { t } = useTranslation();

  const [profilesToDelete, setProfilesToDelete] = useState<BizProfileModels.BizProfileListDto[]>([]);

  const onCloseInner = () => {
    setProfilesToDelete([]);
    onClose();
  };

  const { mutate: bulkDeleteProfiles, isPending: isDeletingProfiles } = BizProfileQueries.useBulkDelete({
    onMutate: async ({ data }) => {
      // Cancel any outgoing refetches for all related queries
      await queryClient.cancelQueries({
        queryKey: [...BizProfileQueries.keys.all],
      });

      // Snapshot the previous value for the current query
      const previousData = queryClient.getQueryData(getProfilesListListQueryKey(pagination));

      // Optimistically update all related list queries
      queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, (old: any) => {
        if (!old?.items) return old;

        return produce(old, (draft: { items?: any[]; totalItems?: number }) => {
          if (draft?.items) {
            const newItems = draft.items.filter((item) => !data.ids.includes(item.id));
            const newTotalItems = Math.max(0, (draft.totalItems || 0) - data.ids.length);
            Object.assign(draft, { items: newItems, totalItems: newTotalItems });
          }
        });
      });

      successToast({
        text: t("uploads.profiles-actions-modal.delete-profiles-success"),
      });

      return { previousData } as { previousData: unknown };
    },
    onError: () => {
      // If the mutation fails, invalidate all queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: [...BizProfileQueries.keys.all],
      });

      errorToast({
        text: t("uploads.userUploads.delete-error"),
      });
    },
    onSuccess: () => {
      // Invalidate all related queries to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: [...BizProfileQueries.keys.all],
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data is in sync
      queryClient.invalidateQueries({
        queryKey: getProfilesListListQueryKey(pagination),
      });
      setProfilesToDelete([]);
      onCloseInner();
    },
  });

  const onDeleteProfiles = () => {
    if (selectAllResumes) {
      const filters = {
        all: true,
        experienceYears: { min: pagination.filterExperienceYearsMin, max: pagination.filterExperienceYearsMax },
        rating: pagination.filterRating,
        msaLocation: pagination.filterMsaLocation,
        tagName: pagination.filterTagName,
        jobTitle: pagination.filterJobTitle,
        search: pagination.filterSearch,
      };
      bulkDeleteProfiles({ data: { filter: filters as BizProfileModels.BizProfileDeleteFilterDto, ids: [] } });
      setSelectAllResumes(false);
      return;
    }
    setProfilesToDelete(selectedProfiles);
    bulkDeleteProfiles({ data: { ids: selectedProfiles.map((profile) => profile.id) } });
  };

  const renderProfiles = profilesToDelete.length > 0 ? profilesToDelete : selectedProfiles;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseInner}
      title={t("uploads.profiles-actions-modal.delete-profiles-title")}
    >
      <div className="flex min-w-[555px] flex-col gap-5 pt-5">
        <Typography
          size="body-3"
          className="text-center"
        >
          {t("uploads.profiles-actions-modal.delete-profiles-description")}
        </Typography>

        <div className="flex max-h-[300px] w-full flex-col overflow-y-auto">
          {renderProfiles?.map((profile, index) => (
            <div
              key={index}
              className="flex items-center border-elevation-outline-1 border-b px-3 py-2-5"
            >
              <Typography size="body-4">{profile.name}</Typography>
            </div>
          ))}
          {selectAllResumes && totalProfiles > 10 && (
            <div className="flex items-center px-3 py-2-5">
              <Typography size="body-4">and {totalProfiles - selectedProfiles.length} more...</Typography>
            </div>
          )}
        </div>

        <div className="flex w-full justify-center gap-5">
          <Button
            onPress={onCloseInner}
            variant="secondary"
            width="auto"
            className="!w-[115px] !px-4 !py-2"
          >
            {t("uploads.profiles-actions-modal.cancel-button")}
          </Button>
          <Button
            onPress={onDeleteProfiles}
            variant="secondary"
            width="auto"
            className="!w-[115px] !border-info-error !bg-info-error !px-4 !py-2"
            isLoading={isDeletingProfiles}
            isDisabled={isDeletingProfiles}
          >
            {t("uploads.profiles-actions-modal.delete-button")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadsPageDeleteProfilesModal;
